/**
 * AssetsPage — single-table list of company-issued assets.
 *
 * HR_ADMIN+ see every asset and can create/edit/archive/assign/return.
 * EMPLOYEE view is auto-filtered to `?assigned_to=me` — read-only.
 *
 * Actions are surfaced via per-row buttons that open modals.
 */
import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Laptop, Archive, UserPlus, Undo2, Pencil } from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";

import {
  listAssets,
  createAsset,
  updateAsset,
  archiveAsset,
  assignAsset,
  returnAsset,
  type Asset,
  type AssetType,
  type AssetStatus,
  type AssetCondition,
} from "@/api/assets";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { SavedViewsMenu } from "@/components/shared/SavedViewsMenu";
import { useSavedViews } from "@/hooks/useSavedViews";

const STATUS_VARIANT: Record<
  AssetStatus,
  "default" | "success" | "warning" | "info" | "danger"
> = {
  AVAILABLE: "success",
  ASSIGNED: "info",
  IN_REPAIR: "warning",
  RETIRED: "default",
  LOST: "danger",
};

const ASSET_TYPES: AssetType[] = [
  "LAPTOP",
  "DESKTOP",
  "MONITOR",
  "PHONE",
  "TABLET",
  "ACCESSORY",
  "FURNITURE",
  "SOFTWARE_LICENSE",
  "OTHER",
];

const CONDITIONS: AssetCondition[] = [
  "NEW",
  "GOOD",
  "FAIR",
  "POOR",
  "BROKEN",
];

export function AssetsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "HR_ADMIN";

  const [assets, setAssets] = useState<Asset[]>([]);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  const savedViews = useSavedViews<{ includeArchived: boolean }>({
    scope: "assets",
    filters: { includeArchived },
    defaultFilters: { includeArchived: false },
    onApply: (f) => setIncludeArchived(f.includeArchived),
  });

  const [showNewModal, setShowNewModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Asset | null>(null);
  const [assignTarget, setAssignTarget] = useState<Asset | null>(null);
  const [returnTarget, setReturnTarget] = useState<Asset | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const [a, emps] = await Promise.all([
          listAssets({ include_archived: includeArchived }),
          // Backend caps per_page at 100. Swallow errors so the assets
          // list still renders even if the directory fetch fails.
          listEmployees(1, 100).catch((err) => {
            console.error("AssetsPage: listEmployees failed", err);
            return {
              items: [] as EmployeeListItem[],
              total: 0,
              page: 1,
              per_page: 100,
              total_pages: 0,
            };
          }),
        ]);
        setAssets(a);
        setEmployees(emps.items);
      } else {
        const a = await listAssets({ assigned_to: "me" });
        setAssets(a);
      }
    } catch {
      addToast("error", "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [addToast, includeArchived, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const employeeNameById = new Map(
    employees.map((e) => [e.id, `${e.first_name} ${e.last_name}`]),
  );

  const columns: Column<Asset>[] = [
    {
      key: "tag",
      header: "Tag",
      render: (a) => (
        <span className="font-mono text-text-primary">{a.asset_tag}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (a) => (
        <span className="inline-flex items-center gap-1 text-text-muted">
          <Laptop className="h-3 w-3" aria-hidden="true" />
          {a.asset_type.replace("_", " ")}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (a) => (
        <Badge variant={STATUS_VARIANT[a.status]}>
          {a.status.replace("_", " ")}
        </Badge>
      ),
    },
    {
      key: "model",
      header: "Model",
      render: (a) => (
        <span className="text-text-muted">
          {a.manufacturer ? `${a.manufacturer} ` : ""}
          {a.model || "—"}
        </span>
      ),
    },
    {
      key: "assigned",
      header: "Assigned To",
      render: (a) => {
        const assn = a.active_assignment;
        if (!assn) return <span className="text-text-muted">—</span>;
        const name = employeeNameById.get(assn.employee_id);
        return (
          <span className="text-text-primary">
            {name || assn.employee_id.slice(0, 8)}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (a) =>
        isAdmin ? (
          <div className="flex items-center gap-1 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditTarget(a)}
              aria-label="Edit asset"
            >
              <Pencil className="h-3 w-3" aria-hidden="true" />
            </Button>
            {a.active_assignment ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setReturnTarget(a)}
                aria-label="Return asset"
              >
                <Undo2 className="h-3 w-3" aria-hidden="true" />
              </Button>
            ) : (
              a.status === "AVAILABLE" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAssignTarget(a)}
                  aria-label="Assign asset"
                >
                  <UserPlus className="h-3 w-3" aria-hidden="true" />
                </Button>
              )
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                if (!window.confirm(`Archive asset ${a.asset_tag}?`)) return;
                try {
                  await archiveAsset(a.id);
                  addToast("success", "Asset archived");
                  load();
                } catch {
                  addToast("error", "Could not archive asset");
                }
              }}
              aria-label="Archive asset"
            >
              <Archive className="h-3 w-3" aria-hidden="true" />
            </Button>
          </div>
        ) : null,
      className: "text-right",
    },
  ];

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Assets"
        subtitle={
          isAdmin
            ? "Track company-issued equipment and licenses."
            : "Equipment assigned to you."
        }
        actions={
          isAdmin ? (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-text-muted">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                />
                Show archived
              </label>
              <SavedViewsMenu
                views={savedViews.views}
                activeView={savedViews.activeView}
                hasUnsavedChanges={savedViews.hasUnsavedChanges}
                isDefault={savedViews.isDefault}
                onApply={savedViews.applyView}
                onSave={savedViews.saveView}
                onDelete={savedViews.deleteView}
                onRename={savedViews.renameView}
                onUpdateActive={savedViews.updateActiveView}
                onClear={savedViews.clear}
              />
              <Button onClick={() => setShowNewModal(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                New Asset
              </Button>
            </div>
          ) : undefined
        }
      />

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <Card padding={false}>
          <DataTable
            columns={columns}
            data={assets}
            keyExtractor={(a) => a.id}
            emptyState={
              isAdmin ? (
                <EmptyState
                  icon={Laptop}
                  title="No assets tracked yet"
                  description="Add laptops, monitors, badges — anything issued to an employee. Tracking them here makes offboarding one click away from a clean return."
                  action={{
                    label: "New Asset",
                    onClick: () => setShowNewModal(true),
                  }}
                />
              ) : (
                <EmptyState
                  icon={Laptop}
                  title="No assets assigned to you"
                  description="When HR hands you a laptop or other gear, it'll show up here with its assignment date and condition."
                />
              )
            }
            caption="Company assets"
          />
        </Card>
      )}

      {showNewModal && (
        <AssetFormModal
          mode="create"
          employees={employees}
          onClose={() => setShowNewModal(false)}
          onSaved={() => {
            setShowNewModal(false);
            addToast("success", "Asset created");
            load();
          }}
        />
      )}
      {editTarget && (
        <AssetFormModal
          mode="edit"
          asset={editTarget}
          employees={employees}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            addToast("success", "Asset updated");
            load();
          }}
        />
      )}
      {assignTarget && (
        <AssignModal
          asset={assignTarget}
          employees={employees}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            setAssignTarget(null);
            addToast("success", "Asset assigned");
            load();
          }}
        />
      )}
      {returnTarget && (
        <ReturnModal
          asset={returnTarget}
          onClose={() => setReturnTarget(null)}
          onReturned={() => {
            setReturnTarget(null);
            addToast("success", "Asset returned");
            load();
          }}
        />
      )}
    </motion.div>
  );
}

function AssetFormModal({
  mode,
  asset,
  employees,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  asset?: Asset;
  employees: EmployeeListItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [assetTag, setAssetTag] = useState(asset?.asset_tag ?? "");
  const [assetType, setAssetType] = useState<AssetType>(
    asset?.asset_type ?? "LAPTOP",
  );
  const [model, setModel] = useState(asset?.model ?? "");
  const [manufacturer, setManufacturer] = useState(asset?.manufacturer ?? "");
  const [serialNumber, setSerialNumber] = useState(
    asset?.serial_number ?? "",
  );
  // Condition is a property of an AssetAssignment, not the Asset row
  // itself (see backend schema). We keep the picker visible but drop it
  // from the create/update payload — the value is applied when the
  // asset is handed to an employee via the assignment endpoint.
  const [condition, setCondition] = useState<AssetCondition>("NEW");
  // "Assigned to" lives on the active AssetAssignment, not the Asset
  // row itself — but exposing it here lets HR change ownership without
  // bouncing between two modals. The handler below diffs the current
  // assignment vs. the picked employee and runs return/assign as
  // needed before saving the asset's own fields.
  //   ""           → not assigned (and: return any current assignment)
  //   "<uuid>"     → assign to that employee (return prior if any)
  const currentAssigneeId = asset?.active_assignment?.employee_id ?? "";
  const [assignedTo, setAssignedTo] = useState<string>(currentAssigneeId);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!assetTag.trim()) return;
    setSaving(true);
    try {
      const payload = {
        asset_tag: assetTag.trim(),
        asset_type: assetType,
        model: model.trim() || null,
        manufacturer: manufacturer.trim() || null,
        serial_number: serialNumber.trim() || null,
      };
      let savedAsset: Asset | null = null;
      if (mode === "create") {
        savedAsset = await createAsset(payload);
      } else if (asset) {
        savedAsset = await updateAsset(asset.id, payload);
      }

      // Reconcile assignment changes after the asset row is saved.
      // Order matters: a return must complete before a re-assign so the
      // service-layer "one active assignment per asset" guard passes.
      if (savedAsset) {
        const before = currentAssigneeId;
        const after = assignedTo;
        if (before !== after) {
          if (before) {
            // Was assigned — return first.
            await returnAsset(savedAsset.id, {
              condition_on_return: condition,
              notes: null,
            });
          }
          if (after) {
            await assignAsset(savedAsset.id, {
              employee_id: after,
              expected_return_at: null,
              condition,
              notes: null,
            });
            addToast(
              "success",
              "Asset assigned — confirmation emails sent to the employee and their manager.",
            );
          } else if (before) {
            addToast("success", "Asset returned");
          }
        }
      }

      onSaved();
    } catch (err) {
      // Surface the backend reason instead of a generic toast so schema
      // drift / permission issues don't silently stay hidden.
      const resp = (err as { response?: { data?: { detail?: unknown } } })
        ?.response;
      const detail = resp?.data?.detail;
      let msg = "Could not save asset";
      if (Array.isArray(detail)) {
        const first = detail[0] as
          | { loc?: unknown[]; msg?: string }
          | undefined;
        if (first?.msg) {
          const field = String(first.loc?.[first.loc.length - 1] ?? "");
          msg = field ? `${field.replace(/_/g, " ")}: ${first.msg}` : first.msg;
        }
      } else if (typeof detail === "string") {
        msg = detail;
      }
      console.error("AssetsPage: save failed", err);
      addToast("error", msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === "create" ? "New asset" : "Edit asset"}
      size="lg"
    >
      <div className="space-y-3">
        <Input
          label="Asset tag"
          value={assetTag}
          onChange={(e) => setAssetTag(e.target.value)}
          placeholder="LT-0123"
        />
        <Select
          label="Type"
          value={assetType}
          onChange={(e) => setAssetType(e.target.value as AssetType)}
          options={ASSET_TYPES.map((t) => ({
            value: t,
            label: t.replace("_", " "),
          }))}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Manufacturer"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            placeholder="Apple"
          />
          <Input
            label="Model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="MacBook Pro 16"
          />
        </div>
        <Input
          label="Serial number"
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
        />
        <Select
          label="Condition"
          value={condition}
          onChange={(e) => setCondition(e.target.value as AssetCondition)}
          options={CONDITIONS.map((c) => ({ value: c, label: c }))}
        />
        <Select
          label="Assigned to"
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
          options={[
            { value: "", label: "— Unassigned —" },
            ...employees.map((e) => ({
              value: e.id,
              label: `${e.first_name} ${e.last_name}`,
            })),
          ]}
        />
        {assignedTo && assignedTo !== currentAssigneeId && (
          <p className="text-xs text-text-muted">
            Saving will assign this asset and email the employee + their
            manager a confirmation.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={!assetTag.trim()}
          >
            {mode === "create" ? "Create" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AssignModal({
  asset,
  employees,
  onClose,
  onAssigned,
}: {
  asset: Asset;
  employees: EmployeeListItem[];
  onClose: () => void;
  onAssigned: () => void;
}) {
  const { addToast } = useToast();
  const [employeeId, setEmployeeId] = useState(employees[0]?.id ?? "");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [condition, setCondition] = useState<AssetCondition>("GOOD");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!employeeId) return;
    setSaving(true);
    try {
      await assignAsset(asset.id, {
        employee_id: employeeId,
        expected_return_at: expectedReturn || null,
        condition,
        notes: notes.trim() || null,
      });
      onAssigned();
    } catch {
      addToast("error", "Could not assign asset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Assign ${asset.asset_tag}`}
      size="lg"
    >
      <div className="space-y-3">
        <Select
          label="Employee"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          options={employees.map((e) => ({
            value: e.id,
            label: `${e.first_name} ${e.last_name}`,
          }))}
        />
        <Input
          label="Expected return"
          type="date"
          value={expectedReturn}
          onChange={(e) => setExpectedReturn(e.target.value)}
        />
        <Select
          label="Condition at handoff"
          value={condition}
          onChange={(e) => setCondition(e.target.value as AssetCondition)}
          options={CONDITIONS.map((c) => ({ value: c, label: c }))}
        />
        <Input
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={!employeeId}
          >
            Assign
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReturnModal({
  asset,
  onClose,
  onReturned,
}: {
  asset: Asset;
  onClose: () => void;
  onReturned: () => void;
}) {
  const { addToast } = useToast();
  const [condition, setCondition] = useState<AssetCondition>("GOOD");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const assn = asset.active_assignment;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await returnAsset(asset.id, {
        condition_on_return: condition,
        notes: notes.trim() || null,
      });
      onReturned();
    } catch {
      addToast("error", "Could not process return");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Return ${asset.asset_tag}`}
      size="lg"
    >
      <div className="space-y-3">
        {assn && (
          <p className="text-sm text-text-muted">
            Currently assigned since {formatDate(assn.assigned_at)}.
          </p>
        )}
        <Select
          label="Condition on return"
          value={condition}
          onChange={(e) => setCondition(e.target.value as AssetCondition)}
          options={CONDITIONS.map((c) => ({ value: c, label: c }))}
        />
        <Input
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={saving}>
            Confirm return
          </Button>
        </div>
      </div>
    </Modal>
  );
}
